import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NewChatButton from '../chat/NewChatButton';
import { useNavigate } from 'react-router-dom';
import { markReportAsViewed } from '../../utils/reportGenerator';

// Define Chat type inline to fix the import error
interface Chat {
  id: string;
  title: string;
  messages: any[];
  createdAt: number;
  updatedAt: number;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  chats: Chat[];
  currentChatId: string | undefined;
  onSelectChat: (chat: Chat) => void;
  onDeleteChat: (chat: Chat) => void;
  onNewChat: () => void;
  onLogout: () => void;
  userName: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMoreChats?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  chats,
  currentChatId,
  onSelectChat,
  onDeleteChat,
  onNewChat,
  onLogout,
  userName,
  isLoading = false,
  onLoadMore,
  hasMoreChats = false
}) => {
  // Add navigate
  const navigate = useNavigate();

  // Handle viewing the report
  const handleViewReport = () => {
    // Just mark as viewed (no-op in the new implementation) and navigate to the report page
    markReportAsViewed();
    navigate('/report');
  };

  // Group chats by date
  const today = new Date().setHours(0, 0, 0, 0);
  const yesterday = new Date(today - 86400000).setHours(0, 0, 0, 0);
  const lastWeek = new Date(today - 604800000).setHours(0, 0, 0, 0);
  const lastMonth = new Date(today - 2629800000).setHours(0, 0, 0, 0);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const groupedChats = {
    today: chats.filter(chat => new Date(chat.updatedAt).setHours(0, 0, 0, 0) === today),
    yesterday: chats.filter(chat => new Date(chat.updatedAt).setHours(0, 0, 0, 0) === yesterday),
    lastWeek: chats.filter(chat => {
      const chatDate = new Date(chat.updatedAt).setHours(0, 0, 0, 0);
      return chatDate < yesterday && chatDate >= lastWeek;
    }),
    lastMonth: chats.filter(chat => {
      const chatDate = new Date(chat.updatedAt).setHours(0, 0, 0, 0);
      return chatDate < lastWeek && chatDate >= lastMonth;
    }),
    older: chats.filter(chat => new Date(chat.updatedAt).setHours(0, 0, 0, 0) < lastMonth)
  };

  return (
    <>
      {/* Custom style for alert icon */}
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-1px) rotate(-1deg); }
            40% { transform: translateX(1px) rotate(1deg); }
            60% { transform: translateX(-1px) rotate(0deg); }
            80% { transform: translateX(1px) rotate(0deg); }
          }
          .alert-icon {
            animation: shake 1.5s infinite;
          }
        `}
      </style>

      {/* Mobile backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-10 md:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={{ x: '-100%' }}
        animate={{ x: isOpen ? 0 : '-100%' }}
        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
        className={`fixed md:relative top-0 left-0 bottom-0 w-80 bg-sidebar-bg z-20 flex flex-col overflow-hidden
                   border-r border-border-color shadow-xl md:shadow-none ${isOpen ? 'block' : 'hidden md:block'}`}
      >
        {/* Headline */}
        <div className="px-6 py-4 text-start border-b border-border-color">
          <h1 className="font-montserrat tracking-wide text-gray-300 flex items-center">
            {/* <span className="text-3xl font-extrabold text-white drop-shadow-[0_0_8px_rgba(99,102,241,0.6)] tracking-wider"></span> */}
            <img src={"/logo.svg"} alt="Agent GG" className="w-12 h-12 mr-2" />
            <span className="text-xl ml-1 font-medium px-1.5 py-0.5 rounded bg-tertiary/50 border border-accent/30">Agent GG</span>
          </h1>
        </div>

        {/* Header */}
        <div className="p-4">
          <NewChatButton onClick={onNewChat} />
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto px-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              <p className="ml-3 text-gray-400">Loading chats...</p>
            </div>
          ) : (
            <>
              {Object.entries(groupedChats).map(([period, periodChats]) =>
                periodChats.length > 0 && (
                  <div key={period} className="py-3">
                    <h3 className="text-xs font-medium text-gray-400 uppercase px-3 pb-2 tracking-wider">
                      {period === 'today' ? 'Today' :
                      period === 'yesterday' ? 'Yesterday' :
                      period === 'lastWeek' ? 'Previous 7 Days' :
                      period === 'lastMonth' ? 'Previous 30 Days' : 'Older'}
                    </h3>
                    <ul className="space-y-1.5">
                      {periodChats.map(chat => (
                        <li key={chat.id} className="group">
                          <button
                            onClick={() => onSelectChat(chat)}
                            className={`w-full text-left transition-all duration-200 py-2.5 px-3.5 rounded-xl flex items-center gap-3
                                      ${chat.id === currentChatId
                                          ? 'bg-gradient-to-r from-gradient-start/10 to-gradient-end/10 text-white border-2 border-black'
                                          : 'hover:bg-tertiary text-gray-300'}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                                className={`w-5 h-5 ${chat.id === currentChatId ? 'stroke-black' : 'stroke-gray-400'}`}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                            </svg>
                            <div className="flex-1 truncate">
                              <p className="truncate">{chat.title}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteChat(chat);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-tertiary rounded-lg text-gray-400 hover:text-white transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                            </button>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}

              {/* Load More Button */}
              {!isLoading && hasMoreChats && onLoadMore && (
                <div className="px-4 py-3 flex justify-center">
                  <button
                    onClick={onLoadMore}
                    className="text-sm font-medium text-accent hover:text-white transition-colors py-2 px-4 rounded-lg border border-accent/30 hover:bg-accent/10"
                  >
                    Load More Chats
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Feature Buttons */}
        <div className="p-4 border-t border-border-color">
          <div className="grid grid-cols-4 gap-2 place-items-center">
            {/* Knowledge Base */}
            <div className="relative group">
              <div
                className="w-10 h-10 rounded-xl bg-tertiary flex items-center justify-center cursor-pointer transition-all border border-border-color hover:bg-gray-600 hover:shadow-lg shadow-accent/20"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/knowledge-base');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-300">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Knowledge Base
              </div>
            </div>

            {/* Settings */}
            <div className="relative group">
              <div
                className="w-10 h-10 rounded-xl bg-tertiary flex items-center justify-center cursor-pointer transition-all border border-border-color hover:bg-gray-600 hover:shadow-lg shadow-accent/20"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/settings');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-300">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Settings
              </div>
            </div>

            {/* Daily Report */}
            <div
              className="relative group cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleViewReport();
              }}
            >
              <div className="w-10 h-10 rounded-xl bg-tertiary flex items-center justify-center cursor-pointer transition-all border border-border-color hover:bg-gray-600 hover:shadow-lg shadow-accent/20">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-300">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                </svg>
              </div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Daily Report
              </div>
            </div>

            {/* Competition Alerts */}
            <div
              className="relative group cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/competition-alerts');
              }}
            >
              <div className="w-10 h-10 rounded-xl bg-tertiary flex items-center justify-center cursor-pointer transition-all border border-border-color hover:bg-gray-600 hover:shadow-lg shadow-accent/20 relative">
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white z-10"></div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-300 alert-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                </svg>
              </div>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-black/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Competition Alerts
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border-color mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-accent/20 flex items-center justify-center text-white">
                {userName.charAt(0).toUpperCase()}
              </div>
              <span className="ml-2 text-sm text-gray-300 truncate max-w-[120px]">
                {userName || 'User'}
              </span>
            </div>
            <button
              onClick={onLogout}
              className="text-gray-400 hover:text-white p-1.5 rounded-md hover:bg-tertiary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
